<!-- components/ImageGallery.vue -->
<template>
  <div class="gallery w-full">
    <!-- Main Carousel -->
    <div
      v-if="mainImage"
      class="gallery__slider relative group"
    >
      <div class="gallery__image">
        <NuxtLink
          class="z-10 cursor-pointer"
          :to="detailUrl"
        >
          <NuxtImg
            :src="mainImage.urlWithProtocol || ''"
            :alt="mainImage.alt || ''"
            class="ao__aside"
            sizes="sm:100vw lg:680px"
          />
        </NuxtLink>
      </div>
      <div
        v-if="mainImage?.title || mainImage?.source"
        :class="isCaptionVisible ? 'gallery-caption--opened' : ''"
        class="gallery-caption"
      >
        <div class="gallery-caption__info" @click="toggleCaption()">
          <DefaultIconsTooltip />
        </div>
        <div
          v-if="isCaptionVisible"
          class="gallery-caption__text"
        >
          <div v-if="mainImage?.title" class="gallery-caption__title">{{ mainImage?.title }}</div>
          <div v-if="mainImage?.title && mainImage?.source" class="gallery-caption__divider" />
          <div v-if="mainImage?.source" class="gallery-caption__source">{{ $t('artobjects.source') }}: {{ mainImage?.source }}</div>
        </div>
      </div>
    </div>

    <!-- Thumbnails -->
    <div
      v-if="images.length > 1"
      class="gallery__thumbnail-container flex max-w-max gap-2 p-2 px-2 rounded-[20px] mt-2">
      <div
        v-for="(image, index) in thumbnailImages"
        :key="index"
        class="gallery__image cursor-pointer"
      >
        <NuxtLink :to="detailUrl">
          <NuxtImg
            :src="image.urlWithProtocol || ''"
            :alt="image.alt || ''"
            class="ao__aside h-[70px]"
            :width="120"
            :height="70"
          />
        </NuxtLink>
      </div>
      <NuxtLink
        v-if="images.length > 1"
        class="gallery__thumbnail-button grow"
        :to="detailUrl"
      >
        <span>
          <DefaultIconsCamera/>
          {{ images.length }}
        </span>
        <span>
          {{ $t('gallery.photos') }}
          <DefaultIconsChevronRight/>
        </span>
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { useWindowSize } from '@vueuse/core'
import type AssetLocal from "~/libs/interfaces/AssetLocal"

const props = defineProps({
  images: { type: Array as PropType<AssetLocal[]>, required: false, default: () => [] },
  detailUrl: { type: String, required: false }
})

const isCaptionVisible = ref(false)

// Reactive window size detection
const { width } = useWindowSize()

// Determine thumbnail count based on screen size and available images
const displayedThumbnailCount = computed(() => {
  return width.value < 640 ? 2 : 4
})

// Get main image
const mainImage = computed(() => {
  return props.images[0] || undefined
})

// Get thumbnails with dynamic count
const thumbnailImages = computed(() => {
  return props.images.slice(1, 1 + displayedThumbnailCount.value)
})

const toggleCaption = () => {
  isCaptionVisible.value = !isCaptionVisible.value
}
</script>